import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';

// Types
export interface Item {
  id: string | number;
  name?: string;
  item?: string;
  [key: string]: any;
}

interface ItemsResponse {
  data: Item[];
  total?: number;
  success?: boolean;
}

interface ItemsState {
  items: Item[];
  total: number;
  soldOutCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: ItemsState = {
  items: [],
  total: 0,
  soldOutCount: 0,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchItemsCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('items/fetchItemsCount', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<ItemsResponse>('/items', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    const items = response.data?.data || [];
    return Array.isArray(items) ? items.length : 0;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch items count'
    );
  }
});

export const fetchSoldOutCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('items/fetchSoldOutCount', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<ItemsResponse>('/items?sold_out=true', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    const items = response.data?.data || [];
    return Array.isArray(items) ? items.length : 0;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch sold out items count'
    );
  }
});


// Slice
const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch items count
      .addCase(fetchItemsCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemsCount.fulfilled, (state, action) => {
        state.loading = false;
        state.total = action.payload;
      })
      .addCase(fetchItemsCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch sold out count
      .addCase(fetchSoldOutCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSoldOutCount.fulfilled, (state, action) => {
        state.loading = false;
        state.soldOutCount = action.payload;
      })
      .addCase(fetchSoldOutCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = itemsSlice.actions;
export default itemsSlice.reducer;
